import { useMemo } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import UserInitials, { UserInitialsType } from "components/comments/UserInitials";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { AiDocumentSource, AiSource } from "../../../components/ai_chat/types";
import { shimmer, spin } from "../../../styles/animations";
import { AnswerMarkdown } from "./AnswerMarkdown";

export type AiQuestionAndAnswerProps = {
  question: string;
  answer: string;
  isLoading: boolean;
  isStreaming?: boolean;
  thinking?: null | string[];
  sources?: AiSource[];
  trackingLinkContext?: (guid: string) => void;
  saveQuestionComponent?: JSX.Element;
  type?: UserInitialsType;
  onDocumentSourceClick?: (source: AiDocumentSource) => void;
};

export function AiQuestionAndAnswer({
  question,
  answer,
  isLoading,
  isStreaming = false,
  thinking,
  sources,
  saveQuestionComponent,
  trackingLinkContext,
  type = "default",
  onDocumentSourceClick,
}: AiQuestionAndAnswerProps) {
  const { data: currentUser } = useCurrentUser();
  const latestThinkingStep = useMemo(() => {
    if (!Array.isArray(thinking) || thinking.length === 0) {
      return null;
    }
    return thinking[thinking.length - 1];
  }, [thinking]);
  const showThinkingStep = isLoading && !!latestThinkingStep;

  return (
    <Flex column gap={24}>
      <UserRow>
        <UserInitials
          firstName={currentUser?.firstName}
          lastName={currentUser?.lastName}
          type={type}
        />
        <UserQuestion>
          <UserHeader>{currentUser?.firstName}</UserHeader>
          <p>{question}</p>
          {saveQuestionComponent}
        </UserQuestion>
      </UserRow>

      <AiLoadingBlock>
        <AiRow>
          <AIAvatar
            type={type}
            aria-label={isStreaming || isLoading ? "AI loading" : "AI answer"}
            isLoading={isStreaming || isLoading}
          >
            <UIcon icon="stars2" color="white" />
          </AIAvatar>
          <AiAnswer>
            <AiHeader>Stotles AI</AiHeader>
            <AiBodyStack>
              <AiBodyLayer
                isVisible={isLoading && !showThinkingStep}
                aria-label="AI loading placeholder"
              >
                <LoadingCard column gap={8}>
                  <SkeletonText
                    width="62%"
                    fontSize={11}
                    lineHeight={1}
                    backgroundColor="sysBorderDecorative"
                    customSpacing={true}
                    animationDelayMs={0}
                  />
                  <SkeletonText
                    width="90%"
                    fontSize={11}
                    lineHeight={1}
                    backgroundColor="sysBorderDecorative"
                    customSpacing={true}
                    animationDelayMs={120}
                  />
                  <SkeletonText
                    width="68%"
                    fontSize={11}
                    lineHeight={1}
                    backgroundColor="sysBorderDecorative"
                    customSpacing={true}
                    animationDelayMs={240}
                  />
                </LoadingCard>
              </AiBodyLayer>

              <AiBodyLayer isVisible={showThinkingStep} aria-label="AI thinking step">
                <ThinkingCard>{latestThinkingStep}</ThinkingCard>
              </AiBodyLayer>

              <AiBodyLayer isVisible={!isLoading} aria-label="AI answer">
                <Flex column gap={10}>
                  <AnswerMarkdown
                    answer={answer || (isStreaming ? "" : "Not found")}
                    trackingLinkContext={trackingLinkContext}
                    sources={sources}
                    isStreaming={isStreaming}
                    onDocumentSourceClick={onDocumentSourceClick}
                    customComponents={{
                      // Custom heading renderer for h1, h2, h3
                      h1: ({ children }) => <NormaliseHeading>{children}</NormaliseHeading>,
                      h2: ({ children }) => <NormaliseHeading>{children}</NormaliseHeading>,
                      h3: ({ children }) => <NormaliseHeading>{children}</NormaliseHeading>,
                    }}
                  />
                </Flex>
              </AiBodyLayer>
            </AiBodyStack>
          </AiAnswer>
        </AiRow>
      </AiLoadingBlock>
    </Flex>
  );
}

function getSize(type: UserInitialsType) {
  switch (type) {
    case "small":
      return { h: 26, w: 26 };
    case "extra-small":
      return { h: 20, w: 20 };
    default:
      return { h: 32, w: 32 };
  }
}

const AIAvatar = styled(Flex)<{ type: UserInitialsType; isLoading?: boolean }>(({
  theme,
  type,
  isLoading,
}) => {
  const { h, w } = getSize(type);
  const ringThickness = type === "default" ? 3 : type === "small" ? 2.5 : 2;
  return {
    height: h,
    width: w,
    borderRadius: "50%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.colors.sysBrandBlueDefault,
    flexShrink: 0,
    position: "relative" as const,
    ...(isLoading && {
      "&::after": {
        content: '""',
        position: "absolute",
        inset: -3,
        borderRadius: "50%",
        border: `${ringThickness}px solid ${theme.colors.sysInfoSubtleAlt}`,
        borderTopColor: theme.colors.sysBrandBlueDefault,
        animation: `${spin} 1s linear infinite`,
        pointerEvents: "none" as const,
        boxSizing: "border-box" as const,
      },
    }),
  };
});

const ThinkingCard = styled.div(({ theme }) => ({
  display: "inline-flex",
  width: "fit-content",
  maxWidth: "100%",
  padding: "8px 12px",
  borderRadius: 6,
  marginBottom: 6,
  color: theme.colors.sysTextSecondary,
  fontSize: "0.9rem",
  animation: `${shimmer} 1.8s ease-in-out infinite`,
  backgroundSize: "200% 100%",
  background: `linear-gradient(90deg, ${theme.colors.sysBackgroundAlternative} 0%, ${theme.colors.sysBackgroundInactive} 50%, ${theme.colors.sysBackgroundAlternative} 100%)`,
}));

const LoadingCard = styled(Flex)(({ theme }) => ({
  padding: "10px 12px",
  borderRadius: 6,
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
  marginBottom: 6,
  minHeight: 58,
  justifyContent: "center",
  animation: `${shimmer} 1.8s ease-in-out infinite`,
  backgroundSize: "200% 100%",
  background: `linear-gradient(90deg, ${theme.colors.sysBackgroundAlternative} 0%, ${theme.colors.sysBackgroundInactive} 50%, ${theme.colors.sysBackgroundAlternative} 100%)`,
}));

const UserRow = styled(Flex)({
  paddingLeft: 3,
  textAlign: "left",
});

const UserQuestion = styled.div({
  paddingLeft: 12,
  width: "-webkit-fill-available",
});

const UserHeader = styled.p({
  fontWeight: 600,
  margin: 0,
});

const AiRow = styled(Flex)({
  paddingLeft: 3,
  textAlign: "left",
});

const AiAnswer = styled.div({
  paddingLeft: 12,
  width: "-webkit-fill-available",
  "& p": { textAlign: "left" },
});

const AiHeader = styled.p({
  fontWeight: 600,
  margin: 0,
});

const AiLoadingBlock = styled.div({
  marginTop: 16,
});

const AiBodyStack = styled.div({
  width: "100%",
  display: "grid",
  gridTemplateAreas: '"stack"',
});

const AiBodyLayer = styled.div<{ isVisible: boolean }>(({ isVisible }) => ({
  gridArea: "stack",
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? "translateY(0px)" : "translateY(-3px)",
  transition: "opacity 260ms ease, transform 260ms ease",
  pointerEvents: isVisible ? "auto" : "none",
}));

const NormaliseHeading = styled(Text)({
  fontSize: "14px !important",
  fontWeight: "700 !important",
  margin: "8px 0px",
});
